import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { Modal, Button } from 'react-bootstrap';
import { getTokenForAuthentication } from './Components/authentication/utils';

const UserStatusChecker = () => {
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const eventSourceRef = useRef(null);
  const popupShownRef = useRef(false);
  const tokenRef = useRef(null);

  const showPopupWithMessage = (message) => {
    setErrorMessage(message);
    setShowModal(true);
    popupShownRef.current = true;
  };

  const checkUserStatus = async (token) => {
    try {
      // Skip check if user is not logged in
      if (!sessionStorage.getItem('email')) return;
      
      const currentSession = await Auth.currentSession();
      console.log("Current session:", currentSession);
      const userEmail = sessionStorage.getItem('email') || '';
      const cognitoUserPool = process.env.REACT_APP_AGENT_USER_POOL || '';
      
      if (!userEmail || !cognitoUserPool || !currentSession) return;

      const response = await axios({
        method: 'post',
        url: process.env.REACT_APP_BASE_ROOT_URL + '/api/v1/csrportal/getuser',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        data: {
          'cognitoUserPool': cognitoUserPool,
          'username': userEmail
        }
      });

      // Check for error in response
      if (response.data.errorCode) {
        setErrorMessage(response.data.message);
        setShowModal(true);
      }
      
      // User exists but is disabled
      if (response.data.enabled === false) {
        setErrorMessage('Your user account has been disabled. Please contact administrator.');
        setShowModal(true);
      } else {
            setShowModal(false);
      }
    } catch (error) {
      // Handle network or other errors
      if (error.response && error.response.data && error.response.data.message) {
        // Check if the error message contains "not found"
        if (String(error.response.data.message).toLowerCase().includes("not found")) {
          // Call checkEmailChange
          checkEmailChange(token);
          
          console.log("User not found error, but keeping SSE connection open for future events");
        } else {
            setErrorMessage(String(error.response.data.message));
            setShowModal(true);
        }
      }
    }
  };

  const checkEmailChange = async (token) => {
    try {
      // Skip if popup is already shown
      if (popupShownRef.current) return;
      
      const userData = JSON.parse(sessionStorage.getItem('USER_DATA') || '{}');
      const sessionEmail = sessionStorage.getItem('email');
      
      if (!userData.agentId || !sessionEmail) return;
      
      const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_BASE_ROOT_URL}/agency/api/v1/agent/${userData.agentId}`,
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.data) {
        const agentData = response.data.data;
        
        // Check if email in the system is different from session email
        if (agentData.email && agentData.email.toLowerCase() !== sessionEmail.toLowerCase()) {
          showPopupWithMessage(`Your email has been changed from ${sessionEmail} to ${agentData.email}. Please login again with your updated email.`);
        }
      }
    } catch (error) {
      console.error("Error checking email change:", error);
    }
  };
  
  useEffect(() => {
    getTokenForAuthentication().then((response) => {
      const token = response['headers'].authorization;
      tokenRef.current = token;
      
      // Initial check
      checkUserStatus(token);
      
      // Set up SSE connection
      const userEmail = sessionStorage.getItem('email');
      if (userEmail) {
        // Properly encode the email to handle special characters
        const encodedEmail = String(userEmail).toLowerCase();
        const sseUrl = `${process.env.REACT_APP_BASE_ROOT_URL}/agency/sse/subscribe/${encodedEmail}`;
        console.log("Connecting to SSE URL:", sseUrl);
        
        try {
          // First try without withCredentials to avoid strict CORS requirements
          const eventSource = new EventSource(sseUrl);
          eventSourceRef.current = eventSource;
          
          console.log("EventSource created successfully");
          
          // Handle initial connection
          eventSource.onopen = () => {
            console.log("SSE connection established successfully");
          };
          
          // Enhanced error handling for CORS issues
          eventSource.onerror = (error) => {
            console.error("SSE connection error:", error);
            console.log("EventSource readyState:", eventSource.readyState);
            
            // If connection was never established, it might be a CORS issue
            if (eventSource.readyState === EventSource.CONNECTING) {
              console.log("Connection failed - likely CORS issue. Check server headers.");
            } else if (eventSource.readyState === EventSource.CLOSED) {
              console.log("Connection closed. Attempting to reconnect...");
            }
            
            // Try to reconnect after error with backoff
            setTimeout(() => {
              if (tokenRef.current) {
                console.log("Trying to reconnect after SSE error...");
                // Close existing connection before reconnecting
                if (eventSourceRef.current) {
                  eventSourceRef.current.close();
                }
                // Just check status instead
                checkUserStatus(tokenRef.current);
              }
            }, 5000);
          };
          
          // Improved message handling with additional logging
          eventSource.onmessage = (event) => {
            console.log("SSE message received (onmessage):", event);
            console.log("Message data:", event.data);
            console.log("Message type:", event.type);
            
            try {
              // For ping messages
              if (event.data === "ping") {
                console.log("SSE ping received");
                // Still trigger checkUserStatus to keep session alive
                if (tokenRef.current) {
                  checkUserStatus(tokenRef.current);
                }
                return;
              }
              
              // Process data and call checkUserStatus
              console.log("Calling checkUserStatus from onmessage handler");
              if (tokenRef.current) {
                checkUserStatus(tokenRef.current);
              }
            } catch (error) {
              console.error("Error handling SSE message:", error);
            }
          };
          
          // Add listeners for all expected event types with better error handling
          const eventTypes = ['INIT', 'message', 'ping', 'status', '12345'];
          eventTypes.forEach(eventType => {
            eventSource.addEventListener(eventType, (event) => {
              console.log(`SSE ${eventType} event received:`, event);
              console.log(`${eventType} event data:`, event.data);
              
              try {
                // Extract data if available
                let data = {};
                try {
                  data = JSON.parse(event.data || '{}');
                  console.log(`Parsed ${eventType} data:`, data);
                  
                  // Save emitterId if present in INIT event
                  if (eventType === 'INIT' && data?.emitterId) {
                    localStorage.setItem("emitterId", data.emitterId);
                    console.log("Stored emitterId:", data.emitterId);
                  }
                } catch (e) {
                  console.log(`Not JSON data in ${eventType} event:`, event.data);
                }
                
                // Always call checkUserStatus regardless of event type
                console.log(`Calling checkUserStatus from ${eventType} event handler`);
                if (tokenRef.current) {
                  checkUserStatus(tokenRef.current);
                }
              } catch (error) {
                console.error(`Error handling SSE ${eventType} event:`, error);
              }
            });
          });
        } catch (error) {
          console.error("Error creating EventSource:", error);
        }
      }
      
      // Cleanup SSE connection on component unmount
      return () => {
        if (eventSourceRef.current) {
          console.log("Closing SSE connection");
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      };
    }).catch(error => {
      console.error("Error getting authentication token:", error);
    });
  }, []);

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    } catch (error) {
      console.error("Error during logout:", error);
      // Force reload even if logout fails
      window.location.href = '/';
    }
  };

  return (
    <Modal 
      show={showModal} 
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>Session Alert</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{errorMessage || 'Your session is no longer valid. Please log in again.'}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button style={{background: "#303192"}} onClick={handleLogout}>
          Logout
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserStatusChecker;